import React, { useReducer, useEffect, useState } from "react";
import PageHeader from "components/PageHeader";
import {
  Header,
  Icon,
  Form,
  Divider,
  Table,
  Select,
  Checkbox,
  Button
} from "semantic-ui-react";
import { maskCurrency, paramsToSearchURL } from "utils";
import history from "services/history";
import request from "services/http";
import { DateTime } from "luxon";
import { numParse, numFormat } from "@rededompedro/utils";
import produce from "immer";
import { toast } from "react-toastify";
import ReactInputMask from "react-input-mask";
import "./Correcoes.scss";

const initialState = {
  empresa: null,
  docto: "",
  dataEmissaoInicio: "",
  dataEmissaoFim: "",
  valor: "",
  vencto: "",
  empresaToChange: null
};

function formReducer(state, action) {
  if (action.type === "reset") {
    return initialState;
  } else {
    return { ...state, [action.type]: action.payload };
  }
}

export default function AlteracaoCentroCusto(props) {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const [empresas, setEmpresas] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);

  useEffect(() => {
    const sub = request({
      method: "GET",
      url: "/financeiro/correcoes/empresas"
    }).subscribe(({ response }) => {
      setEmpresas(response.empresas);
      sub.unsubscribe();
    });
  }, []);

  useEffect(() => {
    setData([]);
    const searchParams = props.location.search;
    if (searchParams.trim() !== "") {
      setLoading(true);
      const sub = request({
        method: "GET",
        url: `/financeiro/correcoes/search-contas-a-pagar${props.location.search}`
      })
        .subscribe(
          ({ response }) => {
            setData(
              response.registros.map(x => {
                x.checked = false;
                x.changed = false;
                return x;
              })
            );
          },
          () => {
            toast.error("Erro ao listar unidades.");
          }
        )
        .add(() => {
          sub.unsubscribe();
          setLoading(false);
        });
    }
  }, [props.location.search]);

  function inputChange(ev, data) {
    const type = data ? data.name : ev.target.name;
    const value = data ? data.value : ev.target.value;
    dispatch({ type, payload: type === "valor" ? maskCurrency(value) : value });
  }

  function submit() {
    let params = {};
    const excludeParams = ["empresaToChange"];
    for (let param in state) {
      switch (param) {
        case "dataEmissaoInicio":
        case "dataEmissaoFim":
        case "vencto":
          if (DateTime.fromFormat(state[param], "dd/MM/yyyy").isValid) {
            params[param] = DateTime.fromFormat(
              state[param],
              "dd/MM/yyyy"
            ).toFormat("yyyy-MM-dd");
          }
          break;

        case "valor":
          if (state[param]) {
            params[param] = numParse(state[param]);
          }
          break;

        default:
          if (
            state[param] &&
            state[param] !== "" &&
            !excludeParams.includes(param)
          ) {
            params[param] = state[param];
          }
      }
    }
    history.push({ search: paramsToSearchURL(params) });
  }

  function checkboxChange(i) {
    return (_, { checked }) => {
      setData(
        produce(data, draft => {
          draft[i].checked = checked;
        })
      );
    };
  }

  function checkAll(_, { checked }) {
    setData(
      produce(data, draft => {
        draft.forEach(d => (d.checked = checked));
      })
    );
  }

  function empresasUpdate() {
    setData(
      produce(data, draft => {
        draft.forEach(d => {
          if (d.checked) {
            d.cod_emp = state.empresaToChange;
            d.changed = true;
          }
        });
      })
    );
  }

  function empresaChange(i) {
    return (_, { value }) => {
      setData(
        produce(data, draft => {
          draft[i].cod_emp = value;
          draft[i].changed = true;
        })
      );
    };
  }

  function finish() {
    setFinishLoading(true);
    const changes = data
      .filter(x => x.changed)
      .map(x => ({ seq: x.seq, empresa: x.cod_emp }));

    const sub = request({
      method: "POST",
      url: "/financeiro/correcoes/change-company-for-regs",
      headers: {
        "Content-Type": "application/json"
      },
      body: { changes }
    })
      .subscribe(
        () => {
          toast.success("Unidades atualizadas com sucesso!");
        },
        () => {
          toast.error("Erro ao atualizar unidades.");
        }
      )
      .add(() => {
        sub.unsubscribe();
        setFinishLoading(false);
      });
  }

  function clearFilter() {
    dispatch({ type: "reset" });
    setData([]);
    history.push({ search: null });
  }

  function markAsChecked(index) {
    return ev => {
      const target = ev.target;
      if (target.tagName === "TD") {
        setData(
          produce(data, draft => {
            draft[index].checked = !draft[index].checked;
          })
        );
      }
    };
  }

  return (
    <div className="page-content">
      <PageHeader />
      <div id="alt-centro-custo" className="subcontent pr-4 pl-4 pb-4">
        <Header as="h3" className="mt-4 ml-4">
          <Icon.Group size="big">
            <Icon name="tasks" className="primary-color" />
          </Icon.Group>
          <Header.Content className="primary-color">
            Alteração de Centro de Custo
            <Header.Subheader className="secondary-color">
              <strong>Correção p/ troca de unidade</strong>
            </Header.Subheader>
          </Header.Content>
        </Header>

        <Divider
          fitted
          style={{ margin: "0 12px 10px 12px", borderTopWidth: "2px" }}
        />

        <Form onSubmit={submit}>
          <Form.Group>
            <Form.Select
              width={3}
              search
              selection
              required
              name="empresa"
              onChange={inputChange}
              label="Empresa"
              selectOnBlur={false}
              placeholder="Selecione..."
              value={state.empresa}
              options={empresas.map(x => ({
                key: x.cod_emp,
                text: x.nome,
                value: x.cod_emp
              }))}
            />
            <Form.Input
              width={2}
              name="docto"
              autoComplete="off"
              onChange={inputChange}
              value={state.docto}
              label="Documento"
            />
            <Form.Field>
              <label>Emissão</label>
              <div className="periodo-inputs">
                <ReactInputMask
                  name="dataEmissaoInicio"
                  mask="99/99/9999"
                  autoComplete="off"
                  value={state.dataEmissaoInicio}
                  onChange={inputChange}
                />
                <ReactInputMask
                  name="dataEmissaoFim"
                  mask="99/99/9999"
                  autoComplete="off"
                  value={state.dataEmissaoFim}
                  onChange={inputChange}
                />
              </div>
            </Form.Field>

            <Form.Field width={2} autoComplete="off">
              <label>Vencimento</label>
              <ReactInputMask
                name="vencto"
                mask="99/99/9999"
                value={state.vencto}
                onChange={inputChange}
              />
            </Form.Field>

            <Form.Input
              width={2}
              name="valor"
              autoComplete="off"
              onChange={inputChange}
              label="Valor"
              value={state.valor}
            />
            <Form.Field style={{ marginTop: 28 }}>
              <Button
                color="blue"
                icon="search"
                loading={loading}
                content="Buscar"
              />
              <Button
                type="button"
                color="yellow"
                icon="erase"
                onClick={clearFilter}
                content="Limpar"
              />
            </Form.Field>
          </Form.Group>
        </Form>

        <Table selectable striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <Checkbox onChange={checkAll} />
              </Table.HeaderCell>
              <Table.HeaderCell>Data de Emissão</Table.HeaderCell>
              <Table.HeaderCell>Documento</Table.HeaderCell>
              <Table.HeaderCell>Unidade</Table.HeaderCell>
              <Table.HeaderCell>Valor</Table.HeaderCell>
              <Table.HeaderCell>Obs.</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {loading && (
              <Table.Row>
                <Table.Cell
                  style={{ padding: 50 }}
                  textAlign="center"
                  colSpan={8}
                >
                  Carregando...
                </Table.Cell>
              </Table.Row>
            )}

            {data.length === 0 && !loading && (
              <Table.Row>
                <Table.Cell
                  style={{ padding: 50 }}
                  textAlign="center"
                  colSpan={8}
                >
                  Faça uma pesquisa para continuar.
                </Table.Cell>
              </Table.Row>
            )}

            {data.length > 0 &&
              data.map((d, i) => (
                <Table.Row
                  key={i}
                  onClick={markAsChecked(i)}
                  className={d.checked ? "checked" : undefined}
                >
                  <Table.Cell>
                    <Checkbox
                      onChange={checkboxChange(i)}
                      checked={d.checked}
                    />
                  </Table.Cell>
                  <Table.Cell>{d.data_emi}</Table.Cell>
                  <Table.Cell>{d.docto}</Table.Cell>
                  <Table.Cell>
                    <Select
                      search
                      selection
                      name="empresa"
                      style={{ width: 250 }}
                      selectOnBlur={false}
                      onChange={empresaChange(i)}
                      value={d.cod_emp}
                      options={empresas.map(x => ({
                        key: x.cod_emp,
                        text: x.nome,
                        value: x.cod_emp
                      }))}
                    />
                  </Table.Cell>
                  <Table.Cell>R$ {numFormat(d.valor)}</Table.Cell>
                  <Table.Cell>{d.obs}</Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>
          {data.length > 0 && (
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan={6}>
                  <Form className="pt-3">
                    <Form.Group>
                      <Form.Select
                        search
                        selection
                        required
                        name="empresaToChange"
                        onChange={inputChange}
                        width={3}
                        selectOnBlur={false}
                        placeholder="Selecione..."
                        value={state.empresaToChange}
                        options={empresas.map(x => ({
                          key: x.cod_emp,
                          text: x.nome,
                          value: x.cod_emp
                        }))}
                      />
                      <Form.Button
                        type="button"
                        icon="exchange"
                        color="blue"
                        content="Atualizar Unidades"
                        disabled={data.filter(x => x.checked).length === 0}
                        onClick={empresasUpdate}
                      />
                      <Form.Field className="ml-auto">
                        <Button
                          icon="check"
                          content="Realizar alterações"
                          color="green"
                          loading={finishLoading}
                          disabled={loading}
                          onClick={finish}
                        />
                      </Form.Field>
                    </Form.Group>
                  </Form>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          )}
        </Table>
      </div>
    </div>
  );
}
